<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t('profile-skills') }}</h4>
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <ul v-if="skills.length > 0" class="skillCategory flex">
          <li v-for="(userSkill,index) in skills" :key="index" class="flex">
            {{ userSkill }}
            <button :id="`skillRemoveBtn${index}`" class="btn remove" @click.stop="onClickOpenDeletePopup(index)">
              <div></div>
            </button>
          </li>
        </ul>
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t('profile-skills') }}</div>
            <div class="selectArea skill">
              <div class="selectBox">
                <input
                  id="skillSearchInput"
                  ref="skillInput"
                  v-model="searchSkillQuery"
                  type="text"
                  :placeholder="t('profile-skills-search-placeholder')"
                  :class="{show: isOpenDropDownFlag}"
                  @click.stop="onClickOpenDropMenu">
                <ul id="skillList" ref="skillSelectArea" class="dropMenu scrollY" :class="{show: isOpenDropDownFlag}">
                  <li v-if="filteredItems.length === 0" class="dropItem">
                    <button type="button" class="dropOption" @click.stop="onAddSkill(searchSkillQuery)">
                      {{ t('profile-skills-search-none', { skill: searchSkillQuery }) }}
                    </button>
                  </li>
                  <li v-for="(skill,index) in filteredItems" v-else :key="index" class="dropItem">
                    <button type="button" class="dropOption" :class="{ active: selectSkillIndex === index}" @click.stop="onAddSkill(skill)">
                      {{ skill }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </ul>
        <div class="btnArea">
          <button class="btn" @click.stop="onClickSave">{{ t('common-save') }}</button>
        </div>
      </div>
    </section>
    <div v-if="isOpenDeletePopup" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div id="skillRemovePopupExitBtn" class="exit" @click.stop="isOpenDeletePopup = false"></div>
        </div>
        <div class="scrollY">
          <h5>{{ t('profile-skills-remove') }}</h5>
          <div class="btnArea">
            <button class="cancel" @click.stop="isOpenDeletePopup = false">{{ t("common-cancel") }}</button>
            <button @click.stop="onClickDelete">{{ t("common-remove") }}</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { onMounted } from "#imports";
import { useToastStore } from "~/store/toast/toastStore";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import { ProfileUserInformationModel } from "~~/package/mapper/profile/model/ProfileUserInformationModel";
import { useProfileStore } from "~/store/profile/profileStore";
import { onClickOutside } from "@vueuse/core";

const { t } = useI18n();

const dialogStore = useDialogStore();
const toastStore = useToastStore();
const profileService = useProfileService();
const profileStore = useProfileStore();

const modifyInfo = ref<ProfileUserInformationModel>(new ProfileUserInformationModel());
const skills = ref<string[]>([...dialogStore.options.props.userSkills]);

const skillSelectArea = ref(null);
const skillInput = ref(null);
const isOpenDropDownFlag = ref(false);
const isOpenDeletePopup = ref(false);

const searchSkillQuery = ref<string | null>("")
const selectSkillIndex = ref<number>(0);
const deleteSkillIndex = ref(-1);

onMounted(() => {
  modifyInfo.value = { ...dialogStore.options.props.modifyInfo };
});

const filteredItems = computed(() =>
  dialogStore.options.props.skills.filter((item: string) => item.toLowerCase().startsWith((searchSkillQuery.value as string).toLowerCase()))
);

const onClickOpenDropMenu = () => {
  if(isOpenDropDownFlag.value){
    onClickCloseDropMenu();
  }
  else {
    isOpenDropDownFlag.value = true;
    window.addEventListener('keydown', handleKeydown);
  }
}

const onClickCloseDropMenu = () => {
  selectSkillIndex.value = 0;
  isOpenDropDownFlag.value = false;
  window.removeEventListener('keydown', handleKeydown);
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown') {
    selectSkillIndex.value = (selectSkillIndex.value + 1) % filteredItems.value.length;
  } else if (event.key === 'ArrowUp') {
    selectSkillIndex.value =
      selectSkillIndex.value === 0 ?
        filteredItems.value.length - 1 : (selectSkillIndex.value - 1 + filteredItems.value.length) % filteredItems.value.length;
  } else if (event.key === 'Enter') {
    searchSkillQuery.value = filteredItems.value.length !== 0 ? filteredItems.value[selectSkillIndex.value] : searchSkillQuery.value
    onAddSkill();
    selectSkillIndex.value = -1;
  }
};

const onAddSkill = (skill?: string) => {
  if(skill)
    skills.value.push(skill as string)
  else
    searchSkillQuery.value ? skills.value.push(searchSkillQuery.value as string) : ""
  skillInput.value.blur();
  searchSkillQuery.value = "";
  onClickCloseDropMenu();
  skills.value = [...new Set(skills.value)]
}

const onClickSave = () => {
  delete modifyInfo.value.aboutMeVOList;
  modifyInfo.value.skills = skills.value.join(',')
  profileService.modifyUserProfile(modifyInfo.value).then(response => {
    if(response.id > 0){
      dialogStore.options.confirm?.event();
      onClickClose();
    }
    else {
      dialogStore.options.close?.event();
    }
  }).catch(() => {
    dialogStore.options.close?.event();
  })
};

const onClickClose = () => {
  dialogStore.close();
};

const onClickOpenDeletePopup = (index: number) => {
  isOpenDeletePopup.value = true;
  deleteSkillIndex.value = index;
};

const onClickDelete = () => {
  isOpenDeletePopup.value = false;
  skills.value.splice(deleteSkillIndex.value, 1);
  toastStore.openToast({
    toastType: ToastType.SUCCESS,
    contents: t(`profile-skills-remove-success`)
  });
}

onClickOutside(skillSelectArea, () => {
  onClickCloseDropMenu();
})
</script>

